import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Loader } from '../../components'
import { previewCycleCount } from '../../services/cycleCountServices'

const T_PREFIX = 'cycle_counts.bulk_assign.preview_step'

export const BulkAssignPreviewStep = ({ warehouseId, filters, stores, onBack, onNext }) => {
  const { i18n } = useTranslation()
  const [previewData, setPreviewData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showSkipped, setShowSkipped] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    const cleanFilters = {}
    if (filters.area?.length) cleanFilters.area = filters.area
    if (filters.row?.length) cleanFilters.row = filters.row
    if (filters.store_id?.length) cleanFilters.store_id = filters.store_id

    previewCycleCount({
      warehouse_id: parseInt(warehouseId),
      filters: cleanFilters,
    })
      .then(data => {
        if (!cancelled) {
          setPreviewData(data)
          setLoading(false)
        }
      })
      .catch(err => {
        if (!cancelled) {
          const errMsg = err.response?.data?.error
          setError(typeof errMsg === 'string' ? errMsg : errMsg?.message || i18n.t('cycle_counts.bulk_assign.error'))
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [warehouseId, filters, i18n])

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader show={true} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500 mb-4">{error}</p>
        <Button type="secondary" onClick={onBack}>
          {i18n.t(`${T_PREFIX}.back`)}
        </Button>
      </div>
    )
  }

  const { summary, breakdown, skipped_locations, skipped_locations_capped } = previewData
  const noLocations = summary.total_matching === 0
  const allAlreadyAssigned = summary.total_matching > 0 && summary.assignable === 0

  // Build filter description
  const filterParts = []
  if (filters.area?.length) filterParts.push(`Area ${filters.area.join(', ')}`)
  if (filters.row?.length) filterParts.push(`Fila ${filters.row.join(', ')}`)
  if (filters.store_id?.length) {
    const storeNames = filters.store_id.map(id => {
      return (stores || []).find(s => s.id === id)?.name || id
    })
    filterParts.push(`Tienda "${storeNames.join(', ')}"`)
  }

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">{i18n.t(`${T_PREFIX}.title`)}</h3>

      {filterParts.length > 0 && (
        <p className="text-sm text-gray-500 mb-4">
          {i18n.t(`${T_PREFIX}.filters_applied`)}: {filterParts.join(', ')}
        </p>
      )}

      {noLocations ? (
        <p className="text-center text-gray-500 py-8">{i18n.t(`${T_PREFIX}.no_locations`)}</p>
      ) : allAlreadyAssigned ? (
        <p className="text-center text-yellow-600 py-8">{i18n.t(`${T_PREFIX}.all_already_assigned`)}</p>
      ) : (
        <>
          <table className="min-w-full divide-y divide-gray-200 mb-4">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t(`${T_PREFIX}.area_row_column`)}</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{i18n.t(`${T_PREFIX}.assignable_column`)}</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{i18n.t(`${T_PREFIX}.already_pending_column`)}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {breakdown.map((row, idx) => (
                <tr key={idx}>
                  <td className="px-4 py-2 text-sm text-gray-900">
                    {row.area} - {row.row}
                  </td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">{row.assignable}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-400">{row.already_pending}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="space-y-1 mb-4">
            <p className="text-sm font-medium text-gray-900">{i18n.t(`${T_PREFIX}.total_assignable`, { count: summary.assignable })}</p>
            {summary.already_pending > 0 && <p className="text-sm text-gray-500">{i18n.t(`${T_PREFIX}.total_skipped`, { count: summary.already_pending })}</p>}
            {summary.to_take_over > 0 && <p className="text-sm text-indigo-600">{i18n.t(`${T_PREFIX}.to_take_over`, { count: summary.to_take_over })}</p>}
          </div>
        </>
      )}

      {skipped_locations && skipped_locations.length > 0 && (
        <div className="mb-4">
          <button className="text-sm text-indigo-600 hover:text-indigo-800 cursor-pointer" onClick={() => setShowSkipped(!showSkipped)}>
            {showSkipped ? i18n.t(`${T_PREFIX}.hide_skipped`) : i18n.t(`${T_PREFIX}.show_skipped`)}
          </button>
          {showSkipped && (
            <div className="mt-2 max-h-40 overflow-y-auto">
              <ul className="text-sm text-gray-500 space-y-1">
                {skipped_locations.map((loc, idx) => (
                  <li key={idx}>
                    <span className="font-mono">{loc.location_code}</span> ({loc.assigned_to})
                  </li>
                ))}
                {skipped_locations_capped && <li className="text-gray-400 italic">{i18n.t(`${T_PREFIX}.and_more`)}</li>}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between pt-4">
        <Button type="secondary" onClick={onBack}>
          {i18n.t(`${T_PREFIX}.back`)}
        </Button>
        <Button type="primary" disabled={!summary || summary.assignable === 0} onClick={() => onNext(previewData)}>
          {i18n.t(`${T_PREFIX}.select_operator`)}
        </Button>
      </div>
    </div>
  )
}
